import { cx, focusRing } from "components/Graphs/utils";
import * as Tooltip from "@radix-ui/react-tooltip";
import React from "react";

import type { ShellUtility } from "../types";

export interface ShellUtilityActionProps {
  utility: ShellUtility;
  onAction: (utilityId: string) => void;
  className?: string;
}

const getAccessibleLabel = (utility: ShellUtility) =>
  utility.unread && !utility.label.toLowerCase().includes("unread")
    ? `${utility.label}, unread`
    : utility.label;

export const ShellUtilityAction: React.FC<ShellUtilityActionProps> = ({
  utility,
  onAction,
  className,
}) => {
  const Icon = utility.icon;
  const tooltipText =
    utility.disabled && utility.disabledReason
      ? utility.disabledReason
      : utility.label;
  const accessibleLabel = getAccessibleLabel(utility);
  const commonProps = {
    "aria-label": accessibleLabel,
    "aria-disabled": utility.disabled ? ("true" as const) : undefined,
    className: cx(
      "group relative inline-flex size-10 shrink-0 items-center justify-center rounded-md border border-transparent text-zinc-400 transition-[background-color,border-color,color,transform] duration-150",
      "hover:border-zinc-700 hover:bg-zinc-900 hover:text-zinc-100",
      "motion-reduce:transition-none",
      utility.disabled &&
        "cursor-not-allowed text-zinc-600 hover:border-transparent hover:bg-transparent hover:text-zinc-600",
      focusRing,
      className,
    ),
  };

  const content = (
    <>
      <Icon className="size-[1.125rem]" strokeWidth={1.7} aria-hidden="true" />
      {utility.unread ? (
        <>
          <span
            className="absolute right-2 top-2 size-1.5 rounded-full bg-highlight ring-2 ring-[#090c0a]"
            aria-hidden="true"
          />
          <span className="sr-only">Unread</span>
        </>
      ) : null}
    </>
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (utility.disabled) {
      event.preventDefault();
      return;
    }

    if (utility.href) return;
    onAction(utility.id);
  };

  return (
    <Tooltip.Provider delayDuration={100}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          {utility.href ? (
            <a
              {...commonProps}
              href={utility.href}
              target="_blank"
              rel="noreferrer"
              onClick={handleClick}
            >
              {content}
            </a>
          ) : (
            <button
              {...commonProps}
              type="button"
              onClick={handleClick}
              data-shell-utility={utility.id}
            >
              {content}
            </button>
          )}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            sideOffset={7}
            collisionPadding={12}
            className="z-[100] max-w-72 rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs leading-4 text-zinc-100 shadow-2xl"
          >
            {tooltipText}
            <Tooltip.Arrow className="fill-zinc-950" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

ShellUtilityAction.displayName = "ShellUtilityAction";

export default ShellUtilityAction;
